import _ from 'lodash';

import { bindCallbacks, statusText } from 'scripts/infrastructure/backends/fake_backend_http/lib/common';

// Answers whose name contains this marker behave as if a V2 chat widget's quick action uses them.
const CHAT_QUICK_ACTION_MARKER = 'chat quick action';
const CHAT_QUICK_ACTION_LANGUAGE = 'en-us';
const CHAT_QUICK_ACTION_MAX_LENGTH = 2048;

export default class SnippetService {
  constructor(database, pubsub) {
    this._pubsub = pubsub;
    this.getDatabase = database;
  }

  fetch(payload, callback, path, { orgId, snippetId }) {
    let snippet = _.find(this.getDatabase(orgId).snippets, { id: snippetId });
    if (!snippet) {
      callback(null, { status: 404, statusText: statusText(404) });
      return;
    }

    callback(null, { status: 200, statusText: statusText(200), response: snippet });
  }

  findAll(payload, callback, path, { orgId }, query) {
    let snippets = this.getDatabase(orgId).snippets;

    if (!query['f.ids']) {
      callback(null, {
        status: 200,
        statusText: statusText(200),
        response: snippets,
      });
      return;
    }

    let ids = query['f.ids'].split(',');
    callback(null, {
      status: 200,
      statusText: statusText(200),
      response: _.filter(snippets, snippet => _.includes(ids, snippet.id)) || [],
    });
  }

  add(payload, callback, path, { orgId }) {
    let snippets = this.getDatabase(orgId).snippets;
    snippets.push(payload);
    callback(null, { status: 204, statusText: statusText(204) });
  }

  update(attrs, callback, path, { orgId, snippetId }) {
    let snippet = _.find(this.getDatabase(orgId).snippets, { id: snippetId });
    const error = usedInChatQuickAction(snippet) && chatQuickActionUpdateError(snippet, attrs);
    if (error) {
      callback(badRequest([error]));
      return;
    }
    _.assign(snippet, attrs);
    callback(null, { status: 204, statusText: statusText(204) });
  }

  delete(teamId, callback, path, { orgId, snippetId }) {
    if (usedInChatQuickAction(_.find(this.getDatabase(orgId).snippets, { id: snippetId }))) {
      callback(badRequest([chatQuickActionError(snippetId, 'in_use', 'answer is used by a chat quick action')]));
      return;
    }
    _.remove(this.getDatabase(orgId).snippets, { id: snippetId });
    this._pubsub.publish(`v1/orgs/${orgId}/snippets/${snippetId}/event/delete`, {});
    callback(null, { status: 204, statusText: statusText(204) });
  }

  getRoutes() {
    return bindCallbacks(
      {
        '/api/v1/orgs/:orgId/snippets': { GET: this.findAll, POST: this.add },
        '/api/v1/orgs/:orgId/snippets/:snippetId': { GET: this.fetch, PATCH: this.update, DELETE: this.delete },
      },
      this
    );
  }
}

function usedInChatQuickAction(snippet) {
  return _.includes(_.toLower(_.get(snippet, 'name')), CHAT_QUICK_ACTION_MARKER);
}

function chatQuickActionUpdateError(snippet, attrs) {
  const bodyHtml = publicBodyHtml(attrs.contents || snippet.contents);
  if (bodyHtml === publicBodyHtml(snippet.contents)) {
    return null;
  }
  if (!bodyHtml.trim()) {
    return chatQuickActionError(snippet.id, 'in_use', 'public content is required by a chat quick action');
  }
  if (bodyHtml.length > CHAT_QUICK_ACTION_MAX_LENGTH) {
    return chatQuickActionError(snippet.id, 'too_long', 'public content is too long for a chat quick action', {
      length: `${bodyHtml.length}`,
      maxLength: `${CHAT_QUICK_ACTION_MAX_LENGTH}`,
    });
  }
  return null;
}

function publicBodyHtml(contents) {
  const content = _.find(contents, { language: CHAT_QUICK_ACTION_LANGUAGE });
  return _.get(content, 'selfService.bodyHtml') || '';
}

function chatQuickActionError(snippetId, code, detail, meta = {}) {
  return {
    code,
    detail,
    meta: {
      type: 'CHAT',
      usage: 'QUICK_ACTION',
      endpointId: 'fake-chat-endpoint',
      address: 'Support Chat',
      answerId: snippetId,
      answerLanguage: CHAT_QUICK_ACTION_LANGUAGE,
      ...meta,
    },
  };
}

function badRequest(errors) {
  return { response: { status: 400, statusText: statusText(400), data: { errors } } };
}
